/**
 * FieldScoutReview — mobile pre-submit summary screen.
 *
 * Spec refs: Implementation Spec §5 / Slice 2 / Task S2-12.
 *
 * PRESENTATIONAL ONLY — no Dexie / axios.
 * Flags are DERIVED via flagFor(value, threshold); callers do not pass flag as a prop.
 *
 * Layout:
 *   Header (dark)  — title: Review · {blockName} · {cultivar?} + progress/date line
 *   Summary        — 3 stat cards: Exceeded (red) / Approaching (amber) / Below (green)
 *   Grouped list   — rows grouped: incidence → level → count; empty groups omitted
 *   Footer         — Submit & sync session button
 *
 * Meter math (§5):
 *   scale = threshold > 0 ? threshold * 1.25 : (value || 1)
 *   fillWidth% = min(100, value / scale * 100)
 *   threshold mark at 80% of track (= threshold / (threshold*1.25) * 100)
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { flagFor } from '../../../scouting/recording';
import type { Flag } from '../../../scouting/recording';
import './FieldScoutReview.css';

// ────────────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────────────

export interface ReviewRow {
  code: string;
  name: string;
  type: 'incidence' | 'level' | 'count';
  value: number;       // pct for incidence, severity for level, count for count
  threshold: number;   // actionThreshold
  display: string;     // pre-rendered value string e.g. "34%", "3/4", "12"
}

export interface FieldScoutReviewProps {
  blockName: string;
  cultivar?: string;
  examined: number;       // points examined
  totalPoints?: number;   // default 100
  date: string;           // ISO or display date
  rows: ReviewRow[];
  onSubmit: () => void;
  submitting?: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ────────────────────────────────────────────────────────────────────────────

const GROUP_ORDER: Array<ReviewRow['type']> = ['incidence', 'level', 'count'];

function meterFillWidth(value: number, threshold: number): number {
  const scale = threshold > 0 ? threshold * 1.25 : (value || 1);
  return Math.min(100, (value / scale) * 100);
}

// ────────────────────────────────────────────────────────────────────────────
// Meter sub-component
// ────────────────────────────────────────────────────────────────────────────

interface MeterProps {
  value: number;
  threshold: number;
  flag: Flag;
}

const Meter: React.FC<MeterProps> = ({ value, threshold, flag }) => {
  const fillWidth = meterFillWidth(value, threshold);

  return (
    <div className="meter">
      <div className="meter__track">
        {/* Threshold mark at 80% of track width */}
        <div className="meter__threshold-mark" style={{ left: '80%' }} />
        <div
          className={`meter__fill meter__fill--${flag}`}
          style={{ width: `${fillWidth}%` }}
        />
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Row sub-component
// ────────────────────────────────────────────────────────────────────────────

interface RowItemProps {
  row: ReviewRow;
}

const RowItem: React.FC<RowItemProps> = ({ row }) => {
  const { t } = useTranslation();
  const flag = flagFor(row.value, row.threshold);

  return (
    <div className="fsr__row" data-testid={`review-row-${row.code}`}>
      <div className="fsr__row-identity">
        <span className="fsr__row-code">{row.code}</span>
        <span className="fsr__row-name">{row.name}</span>
      </div>
      <Meter value={row.value} threshold={row.threshold} flag={flag} />
      <div className="fsr__row-values">
        <span className="fsr__row-display">{row.display}</span>
        <span className="fsr__row-vs">{t('fieldScout.review.vs', { threshold: row.threshold })}</span>
      </div>
      <span className={`fsr__pill fsr__pill--${flag}`}>{t(`fieldScout.review.${flag}`)}</span>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// Stat card sub-component
// ────────────────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  count: number;
  variant: Flag;
  testId: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, count, variant, testId }) => (
  <div className={`fsr__stat fsr__stat--${variant}`}>
    <span className="fsr__stat-count" data-testid={testId}>{count}</span>
    <span className="fsr__stat-label">{label}</span>
  </div>
);

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

export const FieldScoutReview: React.FC<FieldScoutReviewProps> = ({
  blockName,
  cultivar,
  examined,
  totalPoints = 100,
  date,
  rows,
  onSubmit,
  submitting = false,
}) => {
  const { t } = useTranslation();

  // Derive flags once for use in stat counts and rows
  const rowsWithFlags = rows.map(r => ({ ...r, flag: flagFor(r.value, r.threshold) as Flag }));

  const exceededCount   = rowsWithFlags.filter(r => r.flag === 'exceeded').length;
  const approachingCount = rowsWithFlags.filter(r => r.flag === 'approaching').length;
  const belowCount      = rowsWithFlags.filter(r => r.flag === 'below').length;

  // Build title — "Review · {blockName}" + optional " · {cultivar}"
  const titleParts = [t('fieldScout.review.title'), blockName, ...(cultivar ? [cultivar] : [])];
  const title = titleParts.join(' · ');

  return (
    <div className="fsr">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="fsr__header">
        <h2 className="fsr__title">{title}</h2>
        <p className="fsr__subtitle">
          {t('fieldScout.review.pointsOf', { examined, total: totalPoints })} · {date}
        </p>
      </header>

      {/* ── Summary stat cards ──────────────────────────────────────────── */}
      <section className="fsr__summary" aria-label="summary">
        <StatCard
          label={t('fieldScout.review.exceeded')}
          count={exceededCount}
          variant="exceeded"
          testId="stat-exceeded"
        />
        <StatCard
          label={t('fieldScout.review.approaching')}
          count={approachingCount}
          variant="approaching"
          testId="stat-approaching"
        />
        <StatCard
          label={t('fieldScout.review.below')}
          count={belowCount}
          variant="below"
          testId="stat-below"
        />
      </section>

      {/* ── Grouped pest rows ───────────────────────────────────────────── */}
      <section className="fsr__groups" aria-label="pest results">
        {GROUP_ORDER.map(type => {
          const groupRows = rowsWithFlags.filter(r => r.type === type);
          if (groupRows.length === 0) return null;

          return (
            <div key={type} className={`fsr__group fsr__group--${type}`}>
              <h3 className="fsr__group-heading">{t(`fieldScout.review.group.${type}`)}</h3>
              {groupRows.map(row => (
                <RowItem key={row.code} row={row} />
              ))}
            </div>
          );
        })}
      </section>

      {/* ── Footer / submit ─────────────────────────────────────────────── */}
      <footer className="fsr__footer">
        <button
          type="button"
          className="fsr__submit-btn"
          onClick={onSubmit}
          disabled={submitting}
        >
          {submitting ? t('fieldScout.review.submitting') : t('fieldScout.review.submit')}
        </button>
      </footer>
    </div>
  );
};

export default FieldScoutReview;
